<template>
    <h4 class="section-title">{{ translate("federation.rankings") }}</h4>

    <template v-if="initDone">
        <div v-if="events.length === 0" class="has-text-centered py-6">
            <div class="is-size-1 mb-4 has-text-primary" style="opacity: 0.35">
                <i class="fas fa-trophy"></i>
            </div>
            <p class="is-size-5 has-text-weight-semibold mb-2">{{ translate("federation.rankings_no_events") }}</p>
            <p class="is-size-6 has-text-grey">{{ translate("federation.rankings_empty_state") }}</p>
        </div>

        <template v-else>
            <div class="mb-5">
                <LoadingButton
                    :is-loading="merging"
                    class="button is-primary"
                    :disabled="selectedIds.length === 0"
                    @click="mergeAndDownload"
                >
                    <span class="icon"><i class="fas fa-download"></i></span>
                    <span>{{ translate("federation.rankings_merge_button") }}</span>
                </LoadingButton>
            </div>

            <div class="box" v-for="event in events" :key="event.eventId">
                <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                    <span class="has-text-weight-bold is-size-5">{{ event.eventName }}</span>
                    <span class="has-text-grey-light is-flex is-align-items-center event-date">
                        <i class="fas fa-calendar-alt mr-1"></i>
                        <EventDateLabel
                            v-if="event.eventDate"
                            :start-date="event.eventDate"
                            :end-date="event.eventEndDate"
                            :timezone="event.eventTimezone"
                        />
                        <span v-else>—</span>
                    </span>
                </div>
                <div>
                    <div
                        class="px-2 py-2"
                        v-for="ranking in event.rankings"
                        :key="ranking.id"
                        :class="{ 'is-selected': isSelected(ranking.id) }"
                    >
                        <Checkbox2 :model-value="isSelected(ranking.id)" @change="checked => toggleRanking(ranking.id, checked)">
                            <span>
                                {{ ranking.name }}
                                <span class="has-text-grey-light"> ({{ formatDateTime(ranking.created, event.eventTimezone) }}) </span>
                            </span>
                        </Checkbox2>
                    </div>
                </div>
            </div>
        </template>
    </template>

    <div class="columns is-centered is-mobile" v-else>
        <LoadingSpinner2 class="has-added-margin" />
    </div>
</template>

<script setup lang="ts">
    import { onMounted, ref } from "vue";
    import federationsApi, { FederationEventWithRankings } from "../../Api/federationsApi";
    import { useFederationDashboardStore } from "./federationManagementStore";
    import { handleError } from "../../Common/errorHandler";
    import { translate } from "../../Common/translations";
    import { convertToTimezoneDateTimeMed } from "../../Common/dateTimeUtils";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import EventDateLabel from "../../Common/EventDateLabel.vue";

    const store = useFederationDashboardStore();

    const initDone = ref(false);
    const merging = ref(false);
    const events = ref<FederationEventWithRankings[]>([]);
    const selectedIds = ref<number[]>([]);

    onMounted(() => {
        initDone.value = false;
        federationsApi
            .getFederationRankingsEvents(store.federationId)
            .then(result => {
                events.value = result;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });

    function isSelected(id: number): boolean {
        return selectedIds.value.includes(id);
    }

    function toggleRanking(id: number, checked: boolean): void {
        if (checked) {
            selectedIds.value = [...selectedIds.value, id];
        } else {
            selectedIds.value = selectedIds.value.filter(i => i !== id);
        }
    }

    function mergeAndDownload(): void {
        merging.value = true;
        federationsApi
            .mergeFederationRankings(store.federationId, selectedIds.value)
            .then(csvContent => {
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "federation-ranking.csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            })
            .catch(handleError)
            .finally(() => {
                merging.value = false;
            });
    }

    function formatDateTime(dateStr: string, timezone: string): string {
        return convertToTimezoneDateTimeMed(dateStr, timezone);
    }
</script>

<style scoped>
    .event-date {
        white-space: nowrap;
    }
</style>
